/**
 * Formatea una fecha extrayendo directamente del string para evitar conversión de timezone
 * La fecha se muestra exactamente como está guardada en la BD
 * @param {string|Date} value - La fecha a formatear (puede ser string ISO, Date object, etc.)
 * @param {string} [fallback] - Valor a retornar si la fecha es inválida o vacía (default: "")
 * @returns {string} La fecha formateada como DD/MM/YYYY
 */
export function formatDateSync(value, fallback = "") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  try {
    // Si es un string, extraer la fecha directamente sin crear objeto Date
    if (typeof value === "string") {
      // Si tiene formato datetime (YYYY-MM-DDTHH:mm o con timezone), extraer solo la parte de fecha
      if (value.includes("T")) {
        const [datePart] = value.split("T");
        if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = datePart.split("-");
          return `${day}/${month}/${year}`;
        }
      }
      const spacePrefix = value.trim().match(/^(\d{4}-\d{2}-\d{2})(?:\s|$)/);
      if (spacePrefix) {
        const [year, month, day] = spacePrefix[1].split("-");
        return `${day}/${month}/${year}`;
      }
      // Para fecha simple: YYYY-MM-DD -> DD/MM/YYYY
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = value.split("-");
        return `${day}/${month}/${year}`;
      }
    }

    // Si es un objeto Date, usar métodos UTC para evitar conversión de timezone
    if (value instanceof Date) {
      if (isNaN(value.getTime())) {
        return fallback;
      }
      const day = String(value.getUTCDate()).padStart(2, "0");
      const month = String(value.getUTCMonth() + 1).padStart(2, "0");
      const year = value.getUTCFullYear();
      return `${day}/${month}/${year}`;
    }

    // Fallback: intentar parsear como Date y usar UTC
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();
      return `${day}/${month}/${year}`;
    }

    return fallback;
  } catch (error) {
    return fallback;
  }
}

/**
 * Formatea una fecha con hora (DD/MM/YYYY HH:mm).
 * Útil cuando el campo tiene time activo.
 * @param {string|Date} value - La fecha a formatear
 * @param {string} [fallback] - Valor si es inválido (default: "")
 * @returns {string} Fecha y hora formateada
 */
export function formatDateTimeSync(value, fallback = "") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  try {
    if (typeof value === "string") {
      if (value.includes("T")) {
        const [datePart, timePart] = value.split("T");
        if (datePart && datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = datePart.split("-");
          const dateStr = `${day}/${month}/${year}`;
          if (timePart) {
            const timeMatch = timePart.match(/^(\d{1,2}):(\d{2})/);
            if (timeMatch) {
              const h = timeMatch[1].padStart(2, "0");
              const m = timeMatch[2];
              return `${dateStr} ${h}:${m}`;
            }
          }
          return dateStr;
        }
      }
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = value.split("-");
        return `${day}/${month}/${year}`;
      }
    }

    if (value instanceof Date) {
      if (isNaN(value.getTime())) return fallback;
      const day = String(value.getUTCDate()).padStart(2, "0");
      const month = String(value.getUTCMonth() + 1).padStart(2, "0");
      const year = value.getUTCFullYear();
      const h = String(value.getUTCHours()).padStart(2, "0");
      const m = String(value.getUTCMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${h}:${m}`;
    }

    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();
      const h = String(date.getUTCHours()).padStart(2, "0");
      const m = String(date.getUTCMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${h}:${m}`;
    }

    return fallback;
  } catch (error) {
    return fallback;
  }
}

/**
 * YYYY-MM-DD del día civil de un `Date` según la hora local.
 *
 * Es la contracara de `toYyyyMmDdSync`: ese lee un valor guardado (string o Date en
 * UTC) y este lee un `Date` que representa un día del calendario del usuario (p. ej.
 * la celda que tocó en la grilla). Sirve para comparar «día de la UI» con «día del
 * documento» sin pasar por `toDateString()`, que mueve el día según el timezone.
 * @param {Date} date
 * @returns {string} YYYY-MM-DD o "" si no es una fecha válida
 */
export function toLocalYyyyMmDd(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Fecha calendario local YYYY-MM-DD (para comparar con fechas de documento).
 */
export function calendarTodayLocalYyyyMmDd() {
  return toLocalYyyyMmDd(new Date());
}

/**
 * Extrae YYYY-MM-DD de un valor fecha guardado (string ISO o Date) sin reinterpretar el día civil.
 */
export function toYyyyMmDdSync(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  try {
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return "";
      if (s.includes("T")) {
        const [datePart] = s.split("T");
        if (datePart && datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return datePart;
        }
      }
      const spacePrefix = s.match(/^(\d{4}-\d{2}-\d{2})(?:\s|$)/);
      if (spacePrefix) {
        return spacePrefix[1];
      }
      if (s.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return s;
      }
      return "";
    }
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return "";
      const y = value.getUTCFullYear();
      const m = String(value.getUTCMonth() + 1).padStart(2, "0");
      const day = String(value.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    return "";
  } catch (e) {
    return "";
  }
}


/**
 * Formatea un instante (createdAt, updatedAt, fecha de un mail...) en la hora
 * local de quien mira, DD/MM/YYYY HH:mm.
 *
 * Distinto de formatDateTimeSync a propósito: ese muestra el string tal como
 * está guardado, que es lo que corresponde a las fechas de documento (ETD, ETA,
 * vencimientos) porque el día no debe correrse según dónde esté el usuario. Un
 * timestamp, en cambio, es un momento único: guardado en UTC, se ve 12:42 en
 * París y 09:42 en Buenos Aires.
 *
 * Si el valor no trae zona (un "YYYY-MM-DDTHH:mm" suelto, o una fecha sin hora)
 * no hay nada que convertir y se muestra literal.
 *
 * @param {string|Date} value - El instante a formatear
 * @param {string} [fallback] - Valor si es inválido o vacío (default: "")
 * @returns {string} Fecha y hora en la zona del navegador
 */
export function formatTimestampSync(value, fallback = "") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  try {
    // Sin zona explícita no es un instante: se muestra tal cual está guardado.
    if (typeof value === "string" && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim())) {
      return formatDateTimeSync(value, fallback);
    }

    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return formatDateTimeSync(value, fallback);
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const h = String(date.getHours()).padStart(2, "0");
    const m = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${h}:${m}`;
  } catch (error) {
    return fallback;
  }
}
